import type {
  ApiResponse,
  AuthenticatedPilotRegistrationInput,
  CompetitionEventInfo,
  RankingCategory,
} from '@aero-judge/shared';
import { API_VERSION } from '@aero-judge/shared';
import { getAccessToken } from './auth-api';
import type { PublicCompetition, PublicCompetitionList, PublicResults, RoundResults } from './types';

async function publicFetch<T>(
  path: string,
  params?: Record<string, string | number>,
  init?: RequestInit,
): Promise<T> {
  const url = new URL(`/api/${API_VERSION}/public${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, String(value));
    }
  }

  const headers = new Headers(init?.headers);
  const token = getAccessToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Live competition data (registration mode, hero, scores) must not come from
  // the browser HTTP cache. A normal visit from the marketing site would
  // otherwise keep the previous payload until a hard refresh.
  const response = await fetch(url, { ...init, cache: 'no-store', headers });
  const json = (await response.json()) as ApiResponse<T>;

  if (!response.ok || !json.success || json.data === undefined) {
    throw new Error(json.error?.message ?? `Request failed (${response.status})`);
  }

  return json.data;
}

export function competitionPath(competitionId: string, ...segments: string[]): string {
  const base = `/competition/${competitionId}`;
  return segments.length ? `${base}/${segments.join('/')}` : base;
}

/** Browser path including the app base, for anchors outside React Router. */
export function browserPath(path: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '');
  if (!base) return path;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Career page for someone in this competition.
 * A bib number opens the pilot page (back to the pilot list). An AeroJudge id
 * alone opens the person page used by the organizing team.
 */
export function pilotProfilePath(
  competitionId: string,
  pilot: { pilotNumber?: number | null; aeroJudgeId?: string | null },
): string | null {
  if (pilot.pilotNumber != null) {
    return competitionPath(competitionId, 'pilots', String(pilot.pilotNumber));
  }
  if (pilot.aeroJudgeId) {
    return competitionPath(competitionId, 'profile', encodeURIComponent(pilot.aeroJudgeId));
  }
  return null;
}

export function fetchCompetitions(): Promise<PublicCompetitionList> {
  return publicFetch<PublicCompetitionList>('/competitions');
}

export function fetchCompetition(idOrSlug: string): Promise<PublicCompetition> {
  return publicFetch<PublicCompetition>(`/${idOrSlug}`);
}

export function fetchResults(
  idOrSlug: string,
  category: RankingCategory = 'OVERALL',
): Promise<PublicResults> {
  return publicFetch<PublicResults>(`/${idOrSlug}/results`, { category });
}

export interface PublicWind {
  speedMs: number;
  directionDeg: number;
  gustMs: number | null;
  recordedAt: string;
}

export interface PublicRoundStatus {
  id: string;
  number: number;
  name?: string | null;
  type?: string;
  status: string;
  pauseReason?: string | null;
}

export function fetchRoundsStatus(
  idOrSlug: string,
): Promise<{ competitionId: string; rounds: PublicRoundStatus[] }> {
  return publicFetch(`/${idOrSlug}/rounds-status`);
}

export function fetchLatestWind(idOrSlug: string): Promise<PublicWind | null> {
  return publicFetch<PublicWind | null>(`/${idOrSlug}/wind`);
}

export interface PublicFeedItem {
  id: string;
  kind: 'SCORE' | 'REFLIGHT' | 'PHOTO' | 'TEXT';
  pilotNumber: number | null;
  pilotName: string | null;
  pilotPhotoUrl: string | null;
  roundNumber: number | null;
  scoreText: string | null;
  reason: string | null;
  caption: string | null;
  imageUrl: string | null;
  body: string;
  createdAt: string;
}

export function fetchFeed(idOrSlug: string): Promise<PublicFeedItem[]> {
  return publicFetch<PublicFeedItem[]>(`/${idOrSlug}/feed`);
}

export function fetchRoundResults(idOrSlug: string, round: number): Promise<RoundResults> {
  return publicFetch<RoundResults>(`/${idOrSlug}/rounds`, { round });
}

export interface PublicCountry {
  id: string;
  code: string;
  code2: string;
  name: string;
}

export function fetchCountries(): Promise<PublicCountry[]> {
  return publicFetch<PublicCountry[]>('/countries');
}

export interface PublicPilotListItem {
  id: string;
  pilotNumber: number;
  firstName: string;
  lastName: string;
  gender: string;
  nationality: string | null;
  club: string | null;
  glider: string | null;
  status: string;
  isWomen: boolean;
  isJunior: boolean;
  photoUrl?: string | null;
  aeroJudgeId?: string | null;
  country: { name: string; code: string; code2: string } | null;
}

export interface PublicPilotList {
  competitionId: string;
  competitionName: string;
  registrationOpen: boolean;
  pilots: PublicPilotListItem[];
}

export function fetchPilots(idOrSlug: string): Promise<PublicPilotList> {
  return publicFetch<PublicPilotList>(`/${idOrSlug}/pilots`);
}

export interface PublicCareerCompetition {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  venue: string;
  country: string;
  pilotNumber: number | null;
  rank: number | null;
  totalScoreCm: number | null;
  roundsFlown: number | null;
}

export interface PublicCareerWork {
  competitionId: string;
  competitionName: string;
  startDate: string;
  endDate: string;
  venue: string;
  country: string;
  role: string;
}

export interface PublicCareer {
  aeroJudgeId: string | null;
  displayName: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  nationality: { code: string; code2: string; name: string } | null;
  civlId: string | null;
  competitions: PublicCareerCompetition[];
  work: PublicCareerWork[];
}

export function fetchPilotProfile(idOrSlug: string, pilotNumber: number): Promise<PublicCareer> {
  return publicFetch<PublicCareer>(`/${idOrSlug}/pilots/${pilotNumber}/profile`);
}

export function fetchCareer(aeroJudgeId: string): Promise<PublicCareer> {
  return publicFetch<PublicCareer>(`/profiles/${encodeURIComponent(aeroJudgeId)}`);
}

export interface PublicOfficial {
  id: string;
  competitionId: string;
  name: string;
  role: string;
  imageUrl: string | null;
  phone: string | null;
  email: string | null;
  displayOrder: number;
  isPublic: boolean;
  aeroJudgeId?: string | null;
}

export function fetchOfficials(idOrSlug: string): Promise<PublicOfficial[]> {
  return publicFetch<PublicOfficial[]>(`/${idOrSlug}/officials`);
}

export interface PublicSponsor {
  id: string;
  competitionId: string;
  name: string;
  type: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  displayOrder: number;
  isActive: boolean;
}

export function fetchSponsors(idOrSlug: string): Promise<PublicSponsor[]> {
  return publicFetch<PublicSponsor[]>(`/${idOrSlug}/sponsors`);
}

export function fetchEventInfo(idOrSlug: string): Promise<CompetitionEventInfo> {
  return publicFetch<CompetitionEventInfo>(`/${idOrSlug}/info`);
}

export interface RegisteredPilot {
  id: string;
  pilotNumber: number;
  firstName: string;
  lastName: string;
  gender: string;
  nationality: string | null;
  club: string | null;
  glider: string | null;
  status: string;
  photoUrl?: string | null;
  country: { name: string; code: string; code2: string } | null;
  competitionId: string;
  competitionName: string;
}

export function registerPilot(
  idOrSlug: string,
  body: AuthenticatedPilotRegistrationInput,
): Promise<RegisteredPilot> {
  return publicFetch<RegisteredPilot>(`/${idOrSlug}/register`, undefined, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

const PHOTO_MAX_BYTES = 2 * 1024 * 1024;

export async function uploadPilotPhoto(
  idOrSlug: string,
  pilotId: string,
  file: File,
): Promise<{ id: string; pilotNumber: number; photoUrl: string | null }> {
  if (file.size > PHOTO_MAX_BYTES) {
    throw new Error('Photo is too large. Maximum size is 2 MB.');
  }
  const formData = new FormData();
  formData.append('photo', file);
  return publicFetch(`/${idOrSlug}/pilots/${pilotId}/photo`, undefined, {
    method: 'POST',
    body: formData,
  });
}
